import { z } from 'zod';

/**
 * Restaurant form validation schemas
 * For creating and editing restaurants
 */

export const restaurantFormSchema = z.object({
  name: z
    .string()
    .min(2, 'Restaurant name must be at least 2 characters')
    .max(100, 'Restaurant name must be less than 100 characters')
    .trim(),
  description: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional()
    .or(z.literal('')),
  phone: z
    .string()
    .refine((phone) => {
      // Allow empty strings for optional field
      if (!phone || phone.trim() === '') return true;
      
      // Remove all spaces and normalize
      const normalized = phone.replace(/\s+/g, '');
      
      // Supported formats:
      // +919791071560, +91 97910 71560, 97910 71560, 9791071560
      const patterns = [
        /^\+91[6-9]\d{9}$/, // +919791071560
        /^[6-9]\d{9}$/, // 9791071560
      ];
      
      // Also handle spaced format by checking original phone
      const spacedPattern = /^\+91\s\d{5}\s\d{5}$/; // +91 97910 71560
      const spacedPattern2 = /^\d{5}\s\d{5}$/; // 97910 71560
      
      return patterns.some(pattern => pattern.test(normalized)) || 
             spacedPattern.test(phone) || 
             spacedPattern2.test(phone);
    }, 'Please enter a valid phone number (+91 99999 99999)')
    .optional()
    .or(z.literal('')),
  address: z
    .string()
    .max(200, 'Address must be less than 200 characters')
    .optional()
    .or(z.literal('')),
  themeColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Theme color must be a valid hex color')
    .optional(),
});

export type RestaurantFormData = z.infer<typeof restaurantFormSchema>;

/**
 * Extended form data that includes the logo file for form handling
 * This is used internally in forms but not for API calls
 */
export interface RestaurantFormDataWithLogo extends RestaurantFormData {
  logoFile?: File | null;
}
